import { action, computed, observable } from 'mobx'
import { orderBy } from 'lodash-es'
import type {
  GetCurrentUserFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetWorkspaceSelectionFn,
} from '@/features/iam'
import type { LastWorkspaceStorage } from '@/features/iam/workspaces/LastWorkspaceStorage'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { WorkspaceSearcher } from '@/features/iam/workspaces/WorkspaceSearcher'
import type { ActivationLifecycle } from '@/infra/activation'
import { Id } from '@/infra/id'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import type { Storage } from '@/infra/storage'
import { tapAction, toStream } from '@/utils/mobx-util'
import { canModifyLegends, canViewWorkspaceLegends } from './legend-access'

/**
 * Picks the workspace whose legends the legend list shows, among the user's
 * workspaces with legends they may see.
 */
export class WorkspaceSelectorViewModel
  implements WorkspaceSearcher, ActivationLifecycle
{
  private readonly _lifecycle = new LifecycleTracker()

  private readonly _textSearcher = new SearchTextViewModel(500)

  private readonly _globalSnapshotKey = 'globalWorkspaceSnapshot.legends'

  @observable private accessor _workspaceId = Id.none

  @observable
  accessor editing = false

  /**
   * Follows the global workspace selector.
   */
  private readonly _globalWorkspaceChanges$ = toStream(
    () => this._getWorkspaceSelection().workspace?.id,
  ).pipe(
    tapAction((workspaceId) => {
      const workspace = this.workspaces.find((w) => w.id === workspaceId)
      if (workspace) {
        this.selectWorkspace(workspace)
      }
    }),
  )

  constructor(
    private readonly _getCurrentUser: GetCurrentUserFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _getWorkspaceSelection: GetWorkspaceSelectionFn,
    private readonly _lastWorkspaceStorage: LastWorkspaceStorage,
    private readonly _storage: Storage,
  ) {
    this._lifecycle.manage(this._textSearcher.debouncedValue$)
    this._lifecycle.manage(this._globalWorkspaceChanges$)
  }

  /**
   * The memberships of the workspaces whose legends the user may see.
   */
  @computed
  get memberships() {
    return orderBy(
      this._getCurrentUserWorkspaceMemberships().filter((m) =>
        canViewWorkspaceLegends(m, this._getCurrentUser()),
      ),
      (m) => m.workspace.name.toLowerCase(),
    )
  }

  @computed
  get workspaces() {
    return this.memberships.map((m) => m.workspace)
  }

  @computed
  get searchText() {
    return this._textSearcher.value
  }

  @computed
  get filteredWorkspaces() {
    const searchText = this.searchText.toLowerCase()
    return searchText
      ? this.workspaces.filter((ws) =>
          ws.name.toLowerCase().includes(searchText),
        )
      : this.workspaces
  }

  /**
   * The membership of the selected workspace.
   */
  @computed
  get selectedMembership() {
    return (
      this.memberships.find((m) => m.workspace.id === this._workspaceId) ?? null
    )
  }

  @computed
  get selectedWorkspace() {
    return this.selectedMembership?.workspace ?? Workspace.none
  }

  /**
   * Whether the user may change the selected workspace's legends.
   */
  @computed
  get canModifyLegends() {
    return (
      !!this.selectedMembership &&
      canModifyLegends(this.selectedMembership, this._getCurrentUser())
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    this.setDefaultWorkspace()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Selects the workspace the page starts on.
   */
  @action.bound
  setDefaultWorkspace() {
    const find = (workspaceId: string | null | undefined) =>
      this.workspaces.find((w) => w.id === workspaceId)

    // A global workspace picked since the last visit wins over this page's
    // saved selection.
    const globalWorkspaceId = this._getWorkspaceSelection().workspace?.id
    const globalChanged =
      !!globalWorkspaceId &&
      this._storage.getItem(this._globalSnapshotKey) !== globalWorkspaceId
    const userId = this._userId
    const workspace =
      (globalChanged ? find(globalWorkspaceId) : undefined) ??
      (userId ? find(this._lastWorkspaceStorage.load(userId)) : undefined) ??
      find(globalWorkspaceId) ??
      this.workspaces.at(0)

    if (workspace) {
      this.selectWorkspace(workspace)
    }
  }

  @action.bound
  selectWorkspace(workspace: Workspace) {
    this._workspaceId = workspace.id
    const userId = this._userId
    if (workspace.id && userId) {
      this._lastWorkspaceStorage.save(workspace.id, userId)
    }
    // Lets the next visit tell whether the global workspace changed.
    const globalWorkspaceId = this._getWorkspaceSelection().workspace?.id
    if (globalWorkspaceId) {
      this._storage.setItem(this._globalSnapshotKey, globalWorkspaceId)
    }
  }

  @action.bound
  setSearchText(searchText: string) {
    this._textSearcher.change(searchText)
  }

  @action.bound
  startEditing() {
    this.editing = true
  }

  @action.bound
  stopEditing() {
    this.editing = false
  }

  private get _userId() {
    return this._getCurrentUser().publicId
  }
}
